import { createLogger } from '@/lib/logger';

const log = createLogger('ANALYTICS');

export type TimeRange = 'week' | 'month' | 'quarter' | 'year' | 'lifetime';

export interface DateRange {
  startUtcIso: string | null;
  endUtcIsoExclusive: string;
  debugInfo?: {
    timeRange: TimeRange;
    nyLocalStart: string;
    nyLocalEnd: string;
    startUtc: string;
    endUtc: string;
  };
}

function getNYTime(): Date {
  const now = new Date();
  const nyString = now.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const [datePart, timePart] = nyString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
}

function convertNYToUTC(nyDate: Date): Date {
  const nyString = nyDate.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const [datePart, timePart] = nyString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  const utcDateString = `${year}-${month}-${day}T${hour}:${minute}:${second}-05:00`;
  return new Date(utcDateString);
}

export function getAnalyticsDateRange(timeRange: TimeRange, enableDebug = true): DateRange {
  const nowNY = getNYTime();
  const nowUTC = new Date();

  let startNY: Date | null;

  switch (timeRange) {
    case 'week': {
      const dayOfWeek = nowNY.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startNY = new Date(nowNY);
      startNY.setDate(nowNY.getDate() - daysToMonday);
      startNY.setHours(0, 0, 0, 0);
      break;
    }

    case 'month': {
      startNY = new Date(nowNY.getFullYear(), nowNY.getMonth(), 1, 0, 0, 0, 0);
      break;
    }

    case 'quarter': {
      const currentMonth = nowNY.getMonth();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      startNY = new Date(nowNY.getFullYear(), quarterStartMonth, 1, 0, 0, 0, 0);
      break;
    }

    case 'year': {
      startNY = new Date(nowNY.getFullYear(), 0, 1, 0, 0, 0, 0);
      break;
    }

    case 'lifetime': {
      startNY = null;
      break;
    }

    default:
      startNY = new Date(nowNY.getFullYear(), nowNY.getMonth(), 1, 0, 0, 0, 0);
  }

  const startUtcIso = startNY ? convertNYToUTC(startNY).toISOString() : null;
  const endUtcIsoExclusive = nowUTC.toISOString();

  const result: DateRange = {
    startUtcIso,
    endUtcIsoExclusive,
  };

  if (enableDebug) {
    result.debugInfo = {
      timeRange,
      nyLocalStart: startNY ? startNY.toISOString() : 'null (lifetime)',
      nyLocalEnd: nowNY.toISOString(),
      startUtc: startUtcIso || 'null (lifetime)',
      endUtc: endUtcIsoExclusive,
    };

    log.info('📊 Analytics Date Range Debug:', {
      filter: timeRange.toUpperCase(),
      nyLocal: {
        start: startNY ? startNY.toLocaleString() : 'null',
        end: nowNY.toLocaleString(),
      },
      utcIso: {
        start: startUtcIso,
        end: endUtcIsoExclusive,
      },
      note: 'Query uses: gte(start) and lt(end-exclusive)',
    });
  }

  return result;
}

export function getTimeRangeLabel(timeRange: TimeRange): string {
  switch (timeRange) {
    case 'week':
      return 'Week';
    case 'month':
      return 'Month';
    case 'quarter':
      return 'Qtr';
    case 'year':
      return 'Year';
    case 'lifetime':
      return 'All';
    default:
      return 'Unknown';
  }
}
